// services/websocket.js
import { Client } from "@stomp/stompjs";

class WebSocketService {
    constructor() {
        this.client = null;
        this.connected = false;
        this.subscriptions = new Map();
        this.deviceId = null;
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 5;

        // Topics for digital screen
        this.availableTopics = [
            {
                name: 'screen-updates',
                path: (deviceId) => `/topic/digital-screen-updates/${deviceId}`,
                description: 'Screen campaign updates'
            }
            // You can easily add more topics here in the future
            // {
            //     name: 'screen-status',
            //     path: (deviceId) => `/topic/screen-status/${deviceId}`,
            //     description: 'Screen connection status'
            // },
            // {
            //     name: 'campaign-priority',
            //     path: (deviceId) => `/topic/campaign-priority/${deviceId}`,
            //     description: 'Campaign priority changes'
            // }
        ];
    }

    /**
     * Connect to WebSocket and subscribe to topics
     * @param {string} deviceId - Device ID from login (e.g., "DS-20260306-1D7FE4")
     */
    connect(deviceId) {
        if (!deviceId) {
            console.error("❌ No device ID provided for WebSocket connection");
            return;
        }

        this.deviceId = deviceId;

        // Don't reconnect if already connected with same device
        if (this.client && this.connected) {
            //console.log(`WebSocket already connected for device ${deviceId}`);
            return;
        }

        //console.log(`🟢 Connecting WebSocket for device: ${deviceId}`);

        // Create new STOMP client
        this.client = new Client({
            brokerURL: "wss://backend.maamaas.com/promotions/ws",
            // brokerURL: "ws://testing.maamaas.com:8080/promotions/ws",
            reconnectDelay: 5000,
            heartbeatIncoming: 4000,
            heartbeatOutgoing: 4000,
            debug: (str) => {
                if (process.env.NODE_ENV !== 'production') {
                    //console.log("STOMP:", str);
                }
            },
            connectHeaders: {
                'device-id': deviceId
            }
        });

        // Handle successful connection
        this.client.onConnect = () => {
            //console.log("✅ WebSocket Connected Successfully");
            this.connected = true;
            this.reconnectAttempts = 0;

            // Subscribe to all available topics
            this.subscribeToAllTopics();

            // Store connection info
            localStorage.setItem('ws_connected', 'true');
            localStorage.setItem('ws_device', deviceId);

            //console.log(`📡 Subscribed to ${this.availableTopics.length} topics`);
        };

        // Handle STOMP errors
        this.client.onStompError = (frame) => {
            console.error("❌ STOMP Error:", frame.headers["message"]);
            console.error("Details:", frame.body);
        };

        // Handle WebSocket close
        this.client.onWebSocketClose = (event) => {
            //console.log("⚠️ WebSocket Closed:", event);
            this.connected = false;
            localStorage.setItem('ws_connected', 'false');
        };

        // Handle WebSocket errors
        this.client.onWebSocketError = (event) => {
            console.error("🔴 WebSocket Error:", event);
        };

        // Activate connection
        this.client.activate();
    }

    /**
     * Subscribe to all configured topics
     */
    subscribeToAllTopics() {
        if (!this.deviceId) {
            console.error("❌ No device ID available for subscription");
            return;
        }

        // Subscribe to each topic in the availableTopics array
        this.availableTopics.forEach(topicConfig => {
            const topicPath = topicConfig.path(this.deviceId);

            this.subscribe(topicPath, (message) => {
                this.handleMessage(topicConfig.name, topicPath, message);
            });

            //console.log(`  ✅ Subscribed to: ${topicPath} (${topicConfig.description})`);
        });

        // Store subscribed topics in localStorage
        const subscribedPaths = this.availableTopics.map(t => t.path(this.deviceId));
        localStorage.setItem('ws_subscribed_topics', JSON.stringify(subscribedPaths));
    }

    /**
     * Subscribe to a specific topic
     */
    subscribe(topic, handler) {
        if (!this.client || !this.connected) {
            console.warn(`⚠️ Cannot subscribe to ${topic}: WebSocket not connected`);
            return null;
        }

        try {
            const subscription = this.client.subscribe(topic, (message) => {
                try {
                    // Try to parse as JSON
                    let data;
                    try {
                        data = JSON.parse(message.body);
                    } catch {
                        data = message.body;
                    }

                    handler(data);

                } catch (error) {
                    console.error(`❌ Error handling message for ${topic}:`, error);
                }
            });

            // Store subscription
            this.subscriptions.set(topic, subscription);

            return subscription;

        } catch (error) {
            console.error(`❌ Error subscribing to ${topic}:`, error);
            return null;
        }
    }

    /**
     * Handle incoming messages
     */
    handleMessage(topicName, topicPath, message) {
        //console.log(`📦 Message received on ${topicName}:`, message);

        // Store message in localStorage
        this.storeMessage(topicName, message);

        // Trigger custom event for components to listen
        window.dispatchEvent(new CustomEvent('ws-message', {
            detail: {
                topic: topicName,
                topicPath: topicPath,
                data: message,
                timestamp: new Date().toISOString()
            }
        }));
    }

    /**
     * Store messages in localStorage
     */
    storeMessage(topicName, message) {
        try {
            const storageKey = `ws_${topicName}`;

            // Get existing messages
            let existing = [];
            const stored = localStorage.getItem(storageKey);

            if (stored) {
                try {
                    existing = JSON.parse(stored);
                    if (!Array.isArray(existing)) {
                        existing = [];
                    }
                } catch (e) {
                    existing = [];
                }
            }

            // Add new message with timestamp
            const messageWithTime = {
                ...message,
                receivedAt: new Date().toISOString(),
                topic: topicName
            };

            // Keep last 20 messages
            existing.unshift(messageWithTime);
            const toStore = existing.slice(0, 20);
            localStorage.setItem(storageKey, JSON.stringify(toStore));

            // Also store latest message separately for quick access
            localStorage.setItem(`${storageKey}_latest`, JSON.stringify(messageWithTime));

        } catch (error) {
            console.error(`Error storing message for ${topicName}:`, error);
        }
    }

    /**
     * Get messages for a specific topic
     */
    getMessages(topicName) {
        try {
            const storageKey = `ws_${topicName}`;
            return JSON.parse(localStorage.getItem(storageKey) || '[]');
        } catch (error) {
            console.error(`Error getting messages for ${topicName}:`, error);
            return [];
        }
    }

    /**
     * Get latest message for a specific topic
     */
    getLatestMessage(topicName) {
        try {
            const storageKey = `ws_${topicName}_latest`;
            return JSON.parse(localStorage.getItem(storageKey) || 'null');
        } catch (error) {
            console.error(`Error getting latest message for ${topicName}:`, error);
            return null;
        }
    }

    /**
     * Clear messages for a specific topic
     */
    clearMessages(topicName) {
        const storageKey = `ws_${topicName}`;
        localStorage.removeItem(storageKey);
        localStorage.removeItem(`${storageKey}_latest`);
    }

    /**
     * Add a new topic at runtime (for future use)
     */
    addTopic(topicName, pathFunction, description = '') {
        const exists = this.availableTopics.some(t => t.name === topicName);
        if (exists) {
            console.warn(`⚠️ Topic ${topicName} already exists`);
            return false;
        }

        this.availableTopics.push({
            name: topicName,
            path: pathFunction,
            description
        });

        // If already connected, subscribe to this new topic
        if (this.connected && this.deviceId) {
            const topicPath = pathFunction(this.deviceId);
            this.subscribe(topicPath, (message) => {
                this.handleMessage(topicName, topicPath, message);
            });
            //console.log(`✅ Added and subscribed to new topic: ${topicName}`);
        }

        return true;
    }

    /**
     * Remove a topic
     */
    removeTopic(topicName) {
        const index = this.availableTopics.findIndex(t => t.name === topicName);
        if (index === -1) return false;

        // Unsubscribe if connected
        if (this.connected && this.deviceId) {
            const topicPath = this.availableTopics[index].path(this.deviceId);
            this.unsubscribe(topicPath);
        }

        this.availableTopics.splice(index, 1);
        //console.log(`✅ Removed topic: ${topicName}`);
        return true;
    }

    /**
     * Unsubscribe from a specific topic
     */
    unsubscribe(topicPath) {
        if (this.subscriptions.has(topicPath)) {
            const subscription = this.subscriptions.get(topicPath);
            subscription.unsubscribe();
            this.subscriptions.delete(topicPath);
            //console.log(`✅ Unsubscribed from ${topicPath}`);
        }
    }

    /**
     * Disconnect WebSocket (on logout)
     */
    disconnect() {
        //console.log("🔌 Disconnecting WebSocket for device:", this.deviceId);

        // Unsubscribe from all topics
        this.subscriptions.forEach((subscription, topicPath) => {
            try {
                subscription.unsubscribe();
                //console.log(`  ✅ Unsubscribed from ${topicPath}`);
            } catch (error) {
                console.error(`  ❌ Error unsubscribing from ${topicPath}:`, error);
            }
        });

        // Clear subscriptions
        this.subscriptions.clear();

        // Deactivate client
        if (this.client) {
            this.client.deactivate();
            this.client = null;
        }

        // Reset state
        this.connected = false;
        this.deviceId = null;
        this.reconnectAttempts = 0;

        // Clear WebSocket related localStorage
        localStorage.removeItem('ws_connected');
        localStorage.removeItem('ws_device');
        localStorage.removeItem('ws_subscribed_topics');

        //console.log("✅ WebSocket disconnected successfully");
    }

    /**
     * Check if connected
     */
    isConnected() {
        return this.connected;
    }

    /**
     * Get list of all topics
     */
    getTopics() {
        return this.availableTopics.map(t => ({
            ...t,
            currentPath: this.deviceId ? t.path(this.deviceId) : null,
            isSubscribed: this.deviceId ? this.subscriptions.has(t.path(this.deviceId)) : false
        }));
    }

    /**
     * Get current device ID
     */
    getDeviceId() {
        return this.deviceId;
    }
}

// Create singleton instance
const webSocketService = new WebSocketService();
export default webSocketService;